import { useEffect, useRef } from 'react'

export interface UsePollingOptions {
  /** Milliseconds between ticks while polling is active. */
  intervalMs: number
  /** When `false`, no timer runs at all (the "lazy idle" cadence, IB-27 S2 design §4.3, is
   * expressed by the CALLER passing `enabled: false` — this hook has no opinion on why). */
  enabled: boolean
}

/**
 * The first reusable polling primitive in this app (IB-27 S2 design §4.3/§7) — there is no push
 * channel (SSE/WebSocket) anywhere in the frontend to reuse, so gated `setInterval` polling is the
 * deliberate v1 mechanism for any "live-ish" surface built over a plain REST GET. Generic: takes no
 * dependency on the Jobs tray, so a future live surface can adopt it unchanged.
 *
 * Behaviour:
 *  - While `enabled` and the tab is visible, calls `fn()` every `intervalMs`.
 *  - Pauses entirely (`clearInterval`, no ticks) while `document.visibilityState === 'hidden'` — a
 *    backgrounded tab does zero polling work.
 *  - On regaining visibility, calls `fn()` immediately (so the surface is fresh the instant the
 *    user returns, not stale until the next tick) and resumes the interval.
 *  - `fn` is read from a ref on every tick, so callers don't need to memoize it themselves — only
 *    `intervalMs`/`enabled` changes restart the underlying timer.
 */
export function usePolling(fn: () => void, { intervalMs, enabled }: UsePollingOptions): void {
  const fnRef = useRef(fn)
  fnRef.current = fn

  useEffect(() => {
    if (!enabled) return undefined

    let timer: ReturnType<typeof setInterval> | null = null

    function start() {
      if (timer !== null) return
      timer = setInterval(() => fnRef.current(), intervalMs)
    }
    function stop() {
      if (timer === null) return
      clearInterval(timer)
      timer = null
    }
    function handleVisibilityChange() {
      if (document.visibilityState === 'hidden') {
        stop()
      } else {
        fnRef.current()
        start()
      }
    }

    if (document.visibilityState !== 'hidden') {
      start()
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      stop()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [intervalMs, enabled])
}
